import { Component } from '@theme/component';

/**
 * Generic tab-based filter: clicking a tab button (data-filter-value) shows
 * only the items (data-filter-group, space-separated) that match, or all
 * items when the tab's value is "all". Used for the homepage category tabs,
 * the brand directory filter, and the standalone brand list page.
 *
 * Optional: a ref="count" element with a data-template attribute
 * (e.g. "{total}件中 {shown}件を表示") is kept in sync with the visible count.
 *
 * @extends {Component}
 */
class TabFilterComponent extends Component {
  requiredRefs = ['tabs[]', 'items[]'];

  connectedCallback() {
    super.connectedCallback();

    this.refs.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.#activate(tab));
    });

    this.#updateCount(this.refs.items.length);
  }

  #activate(tab) {
    const value = tab.dataset.filterValue;

    this.refs.tabs.forEach((t) => t.classList.remove('active'));
    tab.classList.add('active');

    let shown = 0;
    this.refs.items.forEach((item) => {
      const groups = (item.dataset.filterGroup || '').split(' ');
      const match = value === 'all' || groups.includes(value);

      if (match) {
        shown += 1;

        if (item.hidden) {
          // Unhide first so it re-enters the grid flow, then fade it in
          // on the next frame so the opacity change is transition-animated.
          item.hidden = false;
          item.classList.add('is-filtering-out');
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              item.classList.remove('is-filtering-out');
            });
          });
        } else {
          item.classList.remove('is-filtering-out');
        }
      } else if (!item.hidden) {
        item.classList.add('is-filtering-out');
        item.addEventListener(
          'transitionend',
          () => {
            if (item.classList.contains('is-filtering-out')) item.hidden = true;
          },
          { once: true }
        );
      }
    });

    this.#updateCount(shown);
  }

  #updateCount(shown) {
    const countEl = this.refs.count;
    if (!countEl || !countEl.dataset.template) return;

    countEl.textContent = countEl.dataset.template
      .replace('{total}', this.refs.items.length)
      .replace('{shown}', shown);
  }
}

if (!customElements.get('tab-filter')) {
  customElements.define('tab-filter', TabFilterComponent);
}